// src/components/wishlist/wishlistButton.js
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addToWishlist, removeFromWishlist } from '../../store/slices/wishlistSlice';
import { setError, setSuccess } from '../../store/slices/uiSlice';

const WishlistButton = ({ productId, className = '', showText = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector(state => state.auth);
  // wishlistStatus is populated once by fetchWishlist in App.js on login.
  // No per-product API calls needed — eliminates the 429 burst on product listings.
  const { wishlistStatus, loading } = useSelector(state => state.wishlist);

  const isInWishlist = wishlistStatus[productId] || false;

  const handleToggleWishlist = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!isAuthenticated) {
      navigate('/login?redirect=wishlist');
      return;
    }

    if (isInWishlist) {
      dispatch(removeFromWishlist(productId))
        .unwrap()
        .then(() => dispatch(setSuccess('Product removed from wishlist')))
        .catch((err) => dispatch(setError(err.message || 'Failed to remove from wishlist')));
    } else {
      dispatch(addToWishlist(productId))
        .unwrap()
        .then(() => dispatch(setSuccess('Product added to wishlist')))
        .catch((err) => dispatch(setError(err.message || 'Failed to add to wishlist')));
    }
  };

  const isLoading = loading.add || loading.remove;

  return (
    <button
      onClick={handleToggleWishlist}
      className={`flex items-center ${className}`}
      aria-label={isInWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
      disabled={isLoading}
    >
      {isLoading ? (
        <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-6 w-6 ${isInWishlist ? 'text-red-500 fill-current' : 'text-gray-500'}`}
          viewBox="0 0 24 24"
          stroke="currentColor"
          fill={isInWishlist ? 'currentColor' : 'none'}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={isInWishlist ? 0 : 2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      )}

      {showText && (
        <span className="ml-2">{isInWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}</span>
      )}
    </button>
  );
};

export default WishlistButton;